import { Play, Square, Pencil, XCircle, Eye } from 'lucide-react';
import type { Trip } from '../../types/trip';
import type { UserRole } from '../../types/user';
import { Button } from '../ui/Button';
import { TripStatusBadge } from './TripStatusBadge';
import {
  formatDate,
  formatDesviacion,
  formatTime,
  desviacionInicioMin,
  puedeCancelar,
  puedeCorregir,
  puedeFinalizar,
  puedeIniciar,
} from '../../utils/trips';

interface TripsTableProps {
  trips: Trip[];
  role?: UserRole;
  miDriverId?: string;
  accionEnCurso?: string | null;
  onVerDetalle: (trip: Trip) => void;
  /**
   * Las cuatro acciones son opcionales: una vista de solo lectura (Reportes)
   * simplemente no las pasa. Omitir el callback es lo que oculta el botón —
   * más claro que fingir un rol sin permisos para lograr el mismo efecto.
   */
  onIniciar?: (trip: Trip) => void;
  onFinalizar?: (trip: Trip) => void;
  onCorregir?: (trip: Trip) => void;
  onCancelar?: (trip: Trip) => void;
}

// La última columna es de ANCHO FIJO a propósito. Cada fila es su propio grid,
// así que un `auto` se mide fila por fila: la fila de un viaje `PLANNED` (4
// botones) ensanchaba esa columna y encogía las `1fr` solo en ella, y sus
// celdas quedaban corridas respecto a las demás filas y a la cabecera. 176 px
// alojan el máximo real (iniciar/finalizar + corregir + cancelar + ver).
const GRID = '110px 1fr 110px 1fr 1.6fr 120px 176px';

/**
 * FE-11 — vista densa para quien GESTIONA la operación (Administrador,
 * Supervisor, Ejecutivo). El histórico en tarjetas de "Mi Desempeño" responde
 * otra pregunta ("¿qué hice yo?"); esta responde "¿qué hay programado y qué
 * necesita una decisión ahora".
 *
 * La columna de desviación es la que justifica la tabla: comparar
 * `actualFrom` con `scheduledFrom` en 40 filas de un vistazo es exactamente lo
 * que el contrato hace posible al conservar las dos marcas.
 */
export function TripsTable({
  trips, role, miDriverId, accionEnCurso = null,
  onVerDetalle, onIniciar, onFinalizar, onCorregir, onCancelar,
}: TripsTableProps) {
  return (
    <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: '980px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: '0.75rem', padding: '0.75rem 1rem', background: 'var(--bg-raised)', borderBottom: '1px solid var(--border)', fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
            <span>Estado</span>
            <span>Ventana</span>
            <span>Vehículo</span>
            <span>Conductor</span>
            <span>Origen → Destino</span>
            <span>Salida</span>
            <span style={{ textAlign: 'right' }}>Acciones</span>
          </div>

          {trips.map(trip => {
            const desvioMin = desviacionInicioMin(trip);
            const desvio = formatDesviacion(desvioMin);
            const ocupado = accionEnCurso === trip.id;

            return (
              <div
                key={trip.id}
                id={`trip-row-${trip.id}`}
                // issue #118: `alignItems:'center'` centraba cada celda por
                // separado, y "Ventana"/"Origen → Destino" (dos líneas) vs.
                // "Vehículo"/"Conductor"/"Salida" (una línea) tienen alturas
                // distintas — el centrado dejaba las celdas de una línea a
                // media altura entre las dos líneas de las otras. `start`
                // alinea la PRIMERA línea de todas las celdas contra el
                // mismo borde superior, que es lo que ya hace la cabecera
                // (:55, `stretch` por defecto sobre texto de una sola línea).
                // #191: en el teléfono la tabla hace scroll horizontal y el ojo
                // de "Ver expediente" (última columna) cae bajo el chat-fab fijo
                // abajo-derecha, que lo tapa. La fila entera abre el expediente,
                // así no depende de alcanzar ese botón; los botones de acción
                // detienen la propagación para no disparar el detalle.
                onClick={() => onVerDetalle(trip)}
                style={{ display: 'grid', gridTemplateColumns: GRID, gap: '0.75rem', padding: '0.75rem 1rem', borderBottom: '1px solid var(--border)', alignItems: 'start', fontSize: '12px', color: 'var(--text-secondary)', cursor: 'pointer' }}
              >
                <span><TripStatusBadge status={trip.status} /></span>

                <span style={{ display: 'flex', flexDirection: 'column' }}>
                  <strong style={{ color: 'var(--text-primary)' }}>
                    {formatDate(trip.window?.from ?? trip.scheduledFrom)}
                  </strong>
                  <span style={{ color: 'var(--text-muted)' }}>
                    {trip.window
                      ? `${formatTime(trip.window.from)} – ${formatTime(trip.window.to)}`
                      : `prog. ${formatTime(trip.scheduledFrom)}`}
                  </span>
                </span>

                <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--text-primary)' }}>
                  {trip.vehiclePlate || '—'}
                </span>

                <span>{trip.driverName ?? <em style={{ color: 'var(--text-muted)' }}>sin asignar</em>}</span>

                <span style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{trip.origin}</span>
                  <span style={{ color: 'var(--text-muted)' }}>→ {trip.destination}</span>
                </span>

                <span style={{ fontWeight: 700, color: desvio === undefined ? 'var(--text-muted)' : desvio === 'a tiempo' ? '#10B981' : (desvioMin ?? 0) > 0 ? '#F59E0B' : '#3B82F6' }}>
                  {desvio ?? '—'}
                </span>

                <span style={{ display: 'flex', gap: '0.375rem', justifyContent: 'flex-end' }} onClick={e => e.stopPropagation()}>
                  {onIniciar && puedeIniciar(role, trip, miDriverId) && (
                    <Button size="sm" isLoading={ocupado} onClick={() => onIniciar(trip)} aria-label="Iniciar viaje"><Play size={13} /></Button>
                  )}
                  {onFinalizar && puedeFinalizar(role, trip, miDriverId) && (
                    <Button size="sm" variant="success" isLoading={ocupado} onClick={() => onFinalizar(trip)} aria-label="Finalizar viaje"><Square size={13} /></Button>
                  )}
                  {onCorregir && puedeCorregir(role, trip) && (
                    <Button size="sm" variant="secondary" onClick={() => onCorregir(trip)} aria-label="Corregir viaje"><Pencil size={13} /></Button>
                  )}
                  {onCancelar && puedeCancelar(role, trip) && (
                    <Button size="sm" variant="danger" onClick={() => onCancelar(trip)} aria-label="Cancelar viaje"><XCircle size={13} /></Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => onVerDetalle(trip)} aria-label="Ver expediente"><Eye size={13} /></Button>
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
